import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Brain, Users, BarChart3, MessageSquare, Target, Trophy,
  BookOpen, Sparkles, Shield, ChevronRight,
  Mic, FileText, TrendingUp, Heart, Eye, Zap, Star,
  CheckCircle, ArrowRight, LogIn
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const YAS_LOGO = '/yas-logo.png';
const HERO_IMG = 'https://images.unsplash.com/photo-1758272133833-3a2277d426e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwdXJiYW4lMjB5b3V0aCUyMGdyb3VwJTIwc21pbGluZ3xlbnwwfHx8fDE3ODQ0Mjc5MjF8MA&ixlib=rb-4.1.0&q=85&w=1200';
const MENTOR_IMG = 'https://images.unsplash.com/photo-1758273240360-76b908e7582a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxtZW50b3IlMjBoZWxwaW5nJTIwdGVlbmFnZXJ8ZW58MHx8fHwxNzg0NDI3OTIxfDA&ixlib=rb-4.1.0&q=85&w=800';

const fadeIn = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

const FeatureCard = ({ icon: Icon, title, description, tags, color, delay = 0 }) => (
  <motion.div {...fadeIn} transition={{ delay, duration: 0.4 }}
    className="group p-6 rounded-3xl bg-white border border-black/5 hover:-translate-y-1 hover:shadow-xl"
    style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: color + '18' }}>
      <Icon className="w-6 h-6" style={{ color }} />
    </div>
    <h4 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-lg font-bold text-[#2D2824] mb-2">{title}</h4>
    <p style={{ fontFamily: "'Manrope', sans-serif" }} className="text-sm text-[#2D2824]/60 leading-relaxed mb-3">{description}</p>
    {tags && (
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t, i) => <span key={i} className="text-[10px] font-semibold px-2.5 py-1 rounded-full" style={{ backgroundColor: color + '12', color }}>{t}</span>)}
      </div>
    )}
  </motion.div>
);

const AIShowcasePage = () => {
  const navigate = useNavigate();

  return (
    <div style={{ backgroundColor: '#F5F3EF', fontFamily: "'Manrope', sans-serif" }}>

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #2D2824 0%, #3D3530 60%, #4A3F38 100%)' }}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(2px)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(45,40,36,0.85), rgba(45,40,36,0.95))' }} />
        
        <div className="relative max-w-5xl mx-auto px-6 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center gap-3 mb-6">
                  <img src={YAS_LOGO} alt="YAS Connect" className="w-10 h-10 rounded-xl bg-white p-1" />
                  <span className="text-[#FFD60A] text-xs font-bold tracking-[0.2em] uppercase">AI-Native Platform</span>
                </div>
                <h1 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-4xl md:text-6xl font-extrabold tracking-tighter leading-none mb-5" >
                  <span style={{ color: 'white' }}>Intelligence That</span><br/>
                  <span style={{ color: '#E86C3D' }}>Knows Every Youth</span>
                </h1>
                <p className="text-base md:text-lg leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  14 AI features that learn from every interaction — building a living profile 
                  of each participant that helps them grow and gives you unprecedented insight into your program.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => navigate('/demo')} className="bg-[#E86C3D] hover:bg-[#D55A2D] text-white rounded-full px-7 py-3 text-base font-bold" style={{ transition: 'background 0.2s ease' }}>
                    Request a Demo <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button onClick={() => document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' })}
                    className="rounded-full px-7 py-3 text-base font-bold bg-white/10 text-white hover:bg-white/20 border border-white/20" style={{ transition: 'background 0.2s ease' }}>
                    See How It Works
                  </Button>
                </div>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }}
              className="hidden md:block">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-2 border-white/10">
                <img src={HERO_IMG} alt="Youth community" className="w-full h-80 object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-[#E86C3D]">
        <div className="max-w-5xl mx-auto px-6 py-5 flex flex-wrap justify-center gap-10 text-white text-center">
          {[
            { n: '14', l: 'AI Features' },
            { n: '12+', l: 'Data Sources' },
            { n: '24/7', l: 'AI Career Advisor' },
            { n: '3', l: 'Intelligence Levels' },
          ].map((s, i) => (
            <div key={i}>
              <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-3xl font-extrabold">{s.n}</p>
              <p className="text-xs opacity-90">{s.l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* How It Works */}
      <div id="how" className="max-w-5xl mx-auto px-6 py-20">
        <motion.div {...fadeIn} className="text-center mb-14">
          <span className="text-[#E86C3D] text-xs font-bold tracking-[0.2em] uppercase">How It Works</span>
          <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-3xl md:text-5xl font-extrabold tracking-tight text-[#2D2824] mt-3 mb-4">
            Every Interaction Builds Intelligence
          </h2>
          <p className="text-[#2D2824]/60 max-w-2xl mx-auto">The more your youth use the platform, the smarter it gets. Every tap, every conversation, every goal feeds a growing profile.</p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {[
            { step: '01', title: 'Youth Engages', desc: 'Sets goals, posts wins, chats with AI, completes check-ins', icon: Users, color: '#E86C3D' },
            { step: '02', title: 'Data Captured', desc: '12+ data sources feed into their intelligence profile', icon: FileText, color: '#8B5CF6' },
            { step: '03', title: 'AI Synthesizes', desc: 'GPT-4o creates a living, evolving portrait of each youth', icon: Brain, color: '#FFD60A' },
            { step: '04', title: 'Insights Delivered', desc: 'Personalized guidance for youth, coaching for staff, reports for admins', icon: Sparkles, color: '#4ADE80' },
          ].map((s, i) => (
            <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }} className="text-center">
              <div className="w-16 h-16 rounded-3xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: s.color + '18' }}>
                <s.icon className="w-7 h-7" style={{ color: s.color }} />
              </div>
              <span className="text-xs font-bold" style={{ color: s.color }}>{s.step}</span>
              <h4 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="font-bold text-[#2D2824] mt-1">{s.title}</h4>
              <p className="text-xs text-[#2D2824]/50 mt-1 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* What AI Learns Grid */}
        <motion.div {...fadeIn} className="p-8 rounded-3xl bg-white border border-black/5">
          <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-xl font-bold text-[#2D2824] text-center mb-6">What AI Learns From Each Feature</h3>
          <div className="grid grid-cols-3 md:grid-cols-3 gap-4">
            {[
              { icon: Target, l: 'Goals', d: 'Ambition, planning ability, follow-through', c: '#4ADE80' },
              { icon: CheckCircle, l: 'Check-ins', d: 'Mood trajectory, challenges, growth areas', c: '#3B82F6' },
              { icon: Trophy, l: 'Wins Wall', d: 'Achievement patterns, confidence, social connection', c: '#FFD60A' },
              { icon: BookOpen, l: 'Courses', d: 'Learning interests, knowledge gaps, completion rate', c: '#EC4899' },
              { icon: MessageSquare, l: 'AI Chat', d: 'Unfiltered interests, private concerns, career direction', c: '#8B5CF6' },
              { icon: BarChart3, l: 'Assessment', d: 'Workplace readiness across 6 skill domains', c: '#14B8A6' },
              { icon: Sparkles, l: 'Projects', d: 'Deep aspirations, creative thinking, persistence', c: '#6366F1' },
              { icon: Heart, l: 'Interests', d: 'Career direction, personality, motivation', c: '#EF4444' },
              { icon: Mic, l: 'Voice Input', d: 'Natural expression, authentic communication', c: '#E86C3D' },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-2xl hover:bg-[#F5F3EF]" style={{ transition: 'background 0.2s ease' }}>
                <f.icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: f.c }} />
                <div>
                  <p className="font-bold text-xs text-[#2D2824]">{f.l}</p>
                  <p className="text-[11px] text-[#2D2824]/50 leading-relaxed">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* For Youth */}
      <div className="bg-white">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <motion.div {...fadeIn} className="text-center mb-12">
            <span className="text-[#3B82F6] text-xs font-bold tracking-[0.2em] uppercase">For Youth Participants</span>
            <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-3xl md:text-5xl font-extrabold tracking-tight text-[#2D2824] mt-3 mb-4">
              A Personal AI Coach<br/>for Every Youth
            </h2>
            <p className="text-[#2D2824]/60 max-w-2xl mx-auto">Like having a career advisor, life coach, and study buddy available 24 hours a day — powered by everything the platform knows about them.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard icon={Brain} title="AI Career Discovery" color="#8B5CF6" delay={0}
              description="Analyzes their interests, skills, and activities to suggest careers they've never considered — with salary data, education paths, and personalized 'why this fits you' explanations."
              tags={['Personalized', 'Salary info', 'Education paths']} />
            <FeatureCard icon={MessageSquare} title="AI Career Chat" color="#3B82F6" delay={0.1}
              description="Youth can ask anything about careers, school, or life. The AI remembers their profile and every past conversation — it gets smarter each time they talk."
              tags={['24/7 available', 'Voice input', 'Saves history']} />
            <FeatureCard icon={Target} title="AI Goal Assistant" color="#4ADE80" delay={0.2}
              description="Describe a rough idea → AI turns it into a structured SMART goal with specific milestones and action steps tailored to their life."
              tags={['SMART goals', 'Personalized', 'Calendar sync']} />
            <FeatureCard icon={Sparkles} title="My Story Generator" color="#E86C3D" delay={0.3}
              description="After completing a project workbook, AI weaves their responses into a professional narrative — their story, in their voice, but polished and portfolio-ready."
              tags={['From project data', 'Professional quality']} />
            <FeatureCard icon={Mic} title="Voice Input" color="#FFD60A" delay={0.4}
              description="Youth can SPEAK their wins, goals, and check-in responses instead of typing. We meet them where they are — and capture richer, more authentic data."
              tags={['Talk, don\'t type', 'All features', 'OpenAI Whisper']} />
            <FeatureCard icon={Shield} title="AI Assessment Feedback" color="#14B8A6" delay={0.5}
              description="After each workplace scenario, AI explains why the best answer works and gives personalized coaching — turning every test into a learning moment."
              tags={['Per-question', '6 domains', 'Growth-focused']} />
          </div>
        </div>
      </div>

      {/* For Staff — with image */}
      <div className="max-w-5xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div {...fadeIn}>
              <span className="text-[#4ADE80] text-xs font-bold tracking-[0.2em] uppercase">For Staff & Mentors</span>
              <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#2D2824] mt-3 mb-4">
                Know Every Youth<br/>in 5 Seconds
              </h2>
              <p className="text-[#2D2824]/60 mb-8">No more guessing. Click any youth and AI gives you a complete picture — strengths, challenges, and exactly what to say next.</p>
            </motion.div>
            <div className="space-y-4">
              {[
                { icon: Users, title: 'AI Youth Summary', desc: 'One-click narrative: strengths, growth areas, mood trend, coaching tips', color: '#4ADE80' },
                { icon: Zap, title: 'AI Coach Assistant', desc: 'Priority action, talking points, risk flags, celebration opportunities per youth', color: '#E86C3D' },
                { icon: MessageSquare, title: 'Suggested Responses', desc: 'AI drafts warm, professional answers to youth questions — edit and send', color: '#3B82F6' },
                { icon: Brain, title: 'Knowledge Base', desc: 'Upload program docs → AI answers youth questions directly. Cuts repetitive Qs by 80%.', color: '#8B5CF6' },
              ].map((f, i) => (
                <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }}
                  className="flex gap-4 p-4 rounded-2xl hover:bg-white border border-transparent hover:border-black/5" style={{ transition: 'background 0.2s ease, border-color 0.2s ease' }}>
                  <div className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: f.color + '15' }}>
                    <f.icon className="w-5 h-5" style={{ color: f.color }} />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-[#2D2824]">{f.title}</h4>
                    <p className="text-xs text-[#2D2824]/50">{f.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          <motion.div {...fadeIn} transition={{ delay: 0.2 }} className="hidden md:block">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img src={MENTOR_IMG} alt="Mentor and youth" className="w-full h-96 object-cover" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* For Admins */}
      <div className="bg-white">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <motion.div {...fadeIn} className="text-center mb-12">
            <span className="text-[#E86C3D] text-xs font-bold tracking-[0.2em] uppercase">For Program Administrators</span>
            <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-3xl md:text-5xl font-extrabold tracking-tight text-[#2D2824] mt-3 mb-4">
              Your Entire Program<br/>at a Glance
            </h2>
            <p className="text-[#2D2824]/60 max-w-2xl mx-auto">AI writes your executive summary, identifies problems early, and generates funder-ready reports with real data.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-5">
            <FeatureCard icon={BarChart3} title="AI Program Insights" color="#E86C3D"
              description="Get an engagement grade (A-F), key wins, attention areas, and a 'funder highlight' metric — all AI-generated from real program data."
              tags={['Engagement grade', 'Key wins', 'Funder-ready']} />
            <FeatureCard icon={Eye} title="Youth Deep-Dive + AI" color="#8B5CF6" delay={0.1}
              description="Click any youth → see their complete activity PLUS an AI-generated summary with strengths, growth areas, and their full AI conversation history."
              tags={['Full activity', 'AI narrative', 'Chat history']} />
            <FeatureCard icon={TrendingUp} title="AI Usage Analytics" color="#3B82F6" delay={0.2}
              description="Dashboard showing how youth use AI: total conversations, topic breakdown, top users, and trends — proving AI's impact to funders."
              tags={['Usage rate', 'Topics', 'Top users']} />
            <FeatureCard icon={FileText} title="AI-Enhanced Reports" color="#4ADE80" delay={0.3}
              description="PDF reports with AI-written narratives. The Funder Impact Summary writes itself from real data — no more manual report writing."
              tags={['PDF + CSV', 'AI narratives', 'One-click']} />
          </div>
        </div>
      </div>

      {/* The Living Profile */}
      <div style={{ background: '#2D2824' }} className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeIn} className="text-center mb-12">
            <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: 'white' }} className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4">
              The Living Youth Profile
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)' }} className="max-w-2xl mx-auto">
              Every week, the AI learns more. By the end of a program, you know each youth better than any paper file ever could.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { week: 'Week 1', items: ['Basic identity', 'Initial interests', 'First check-in mood'], pct: 15, color: '#FFD60A' },
              { week: 'Week 4', items: ['Goal patterns', 'Mood trajectory', 'Career direction', 'Assessment scores', 'AI chat insights'], pct: 55, color: '#E86C3D' },
              { week: 'Week 8', items: ['Full growth narrative', 'Pre/post comparison', 'Behavioral patterns', 'Predictive insights', 'Career readiness'], pct: 95, color: '#4ADE80' },
            ].map((w, i) => (
              <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.15 }}
                className="p-6 rounded-3xl bg-white/8 backdrop-blur border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h4 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: 'white' }} className="font-bold text-lg">{w.week}</h4>
                  <span className="text-sm font-bold" style={{ color: w.color }}>{w.pct}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full mb-5 overflow-hidden">
                  <motion.div className="h-full rounded-full" style={{ backgroundColor: w.color }}
                    initial={{ width: 0 }} whileInView={{ width: `${w.pct}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3 }} />
                </div>
                <ul className="space-y-2">
                  {w.items.map((item, j) => (
                    <li key={j} className="text-sm flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: w.color }} /> {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Privacy */}
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <Shield className="w-14 h-14 mx-auto mb-5 text-[#3B82F6]" />
        <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-2xl md:text-3xl font-extrabold text-[#2D2824] mb-3">Built with Privacy in Mind</h2>
        <p className="text-[#2D2824]/60 mb-8 leading-relaxed">AI provides insights for humans to act on — it never makes decisions. Mood data is for support, not punishment. Low engagement triggers outreach, not discipline.</p>
        <div className="grid grid-cols-3 gap-4">
          {[
            { role: 'Youth', sees: 'Own data + AI career guidance' },
            { role: 'Staff', sees: 'Assigned youth + AI coaching' },
            { role: 'Admin', sees: 'All program data + AI analytics' },
          ].map((r, i) => (
            <div key={i} className="p-5 rounded-3xl bg-white border border-black/5">
              <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="font-bold text-[#2D2824] mb-1">{r.role}</p>
              <p className="text-xs text-[#2D2824]/50">{r.sees}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="bg-[#E86C3D] py-20 px-6 text-center">
        <motion.div {...fadeIn}>
          <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: 'white' }} className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4">
            Ready to See AI in Action?
          </h2>
          <p className="text-lg mb-8" style={{ color: 'rgba(255,255,255,0.85)' }}>
            Schedule a demo and we'll show you exactly how YAS Connect transforms your program.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button onClick={() => navigate('/demo')} className="bg-white text-[#E86C3D] hover:bg-gray-100 rounded-full px-8 py-3 text-lg font-bold shadow-lg" style={{ transition: 'background 0.2s ease' }}>
              Request a Demo <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button onClick={() => navigate('/login')} className="rounded-full px-8 py-3 text-lg font-bold bg-white/15 text-white hover:bg-white/25 border border-white/30" style={{ transition: 'background 0.2s ease' }}>
              <LogIn className="w-5 h-5 mr-2" /> Log In
            </Button>
          </div>
          <div className="flex items-center justify-center gap-2 mt-8">
            <img src={YAS_LOGO} alt="" className="w-7 h-7 rounded-lg bg-white p-0.5" />
            <span className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>YAS Connect — AI-Native Youth Development</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AIShowcasePage;
